import {
	Component,
	Input,
	ContentChildren,
	QueryList,
	ElementRef,
	AfterContentInit
} from '@angular/core';

import styles from './vb-ui-toggle-content-display.module.less';

/**
 * This component is used to show "show more/show less" button with toggling of content display.
 * It can be configured in two ways.
 * -------------------------------------------------------
 * To limit by number of items:
 * <vb-ui-toggle-content-display
			[minItems]="4"
			[totalItems]="10"
			<div *ngFor="..."
				#vbUiToggleItem>
				...
			</div>
 *	</vb-ui-toggle-content-display>
 *-----------------------------------------------------------
 * 2. to limit size by height:
 * <vb-ui-toggle-content-display
		[minHeight]="200">
		<div vbUiToggleItem>
			...
		</div>
	</vb-ui-toggle-content-display>
 */

@Component({
	selector: 'vb-ui-toggle-content-display',
	template: `
		<div>
			<div [ngClass]="styles.contentWrapper"
				[style.maxHeight]="heightStyle"
				[style.overflow]="overflowStyle">
				<ng-content></ng-content>
			</div>

			<div
				[ngClass]="styles.toggleBtnWapper"
				*ngIf="showToggle">
				<button type="button"
					[ngClass]="styles.toggleBtn"
					(click)="onToggleClick()">
					<span>
						{{ (isExpanded ? 'ShowLess' : 'ShowMore') | translate }}
					</span>
				</button>
			</div>
		</div>
	`,
	standalone: false
})
export class VbUiToggleContentDisplayComponent implements AfterContentInit {
	@Input() private readonly minHeight: number;
	@Input() private readonly minItems: number;
	@Input() private readonly totalItems: number;

	public readonly styles = styles;

	public isExpanded: boolean = false;
	public overflowStyle: string = 'visible';
	public heightStyle: string = 'auto';

	@ContentChildren('vbUiToggleItem', { descendants: true })
	public contentItems: QueryList<ElementRef>;

	public ngAfterContentInit(): void {
		this.updateDisplay();

		if(this.minItems) {
			this.contentItems.changes.subscribe(() => this.updateContentItems());
		}
	}

	public onToggleClick(): void {
		this.isExpanded = !this.isExpanded;
		this.updateDisplay();
	}

	private updateDisplay(): void {
		if (this.minItems) {
			this.updateContentItems();
		} else if (this.isExpanded) {
			this.overflowStyle = 'visible';
			this.heightStyle = 'none';
		} else {
			this.overflowStyle = 'hidden';
			this.heightStyle = this.minHeight + 'px';
		}
	}

	private updateContentItems(): void {
		this.contentItems.forEach((item, i) => {
			const el = item.nativeElement as HTMLElement;
			if (i >= this.minItems && !this.isExpanded) {
				el.classList.add('ng-hide');
			}
			else {
				el.classList.remove('ng-hide');
			}
		});
	}

	public get showToggle(): boolean {
		const el = this.contentItems.first?.nativeElement;

		return this.minHeight
			? el?.scrollHeight > this.minHeight
			: this.totalItems > this.minItems;
	}
}
